import { logoutWithRefreshToken } from '../api'
import router from '../router'

const TOKEN_KEY = 'LoginToken'
const LAST_ACTIVITY_KEY = 'lastActivity'
const REMEMBER_KEY = 'QTrackRememberMe'

class TokenManager {
  constructor() {
    this.token = null
    this.refreshPromise = null
    this.redirectingToLogin = false
    this.setupActivityMonitor()
  }

  getToken() {
    let sessionToken = sessionStorage.getItem(TOKEN_KEY)
    const legacyToken = localStorage.getItem(TOKEN_KEY)
    if (!sessionToken && legacyToken) {
      sessionToken = legacyToken
      sessionStorage.setItem(TOKEN_KEY, legacyToken)
      localStorage.removeItem(TOKEN_KEY)
      localStorage.setItem(REMEMBER_KEY, 'true')
    }
    if (!sessionToken) {
      this.token = null
      return null
    }
    if (sessionToken !== this.token) {
      this.token = sessionToken
    }
    return this.token
  }

  setToken(token, rememberMe = this.isRemembered()) {
    this.token = token
    this.redirectingToLogin = false
    sessionStorage.setItem(TOKEN_KEY, token)
    localStorage.removeItem(TOKEN_KEY)
    this.setRememberMe(rememberMe)
    this.updateLastActivity()
  }

  clearToken(clearRemember = true) {
    this.token = null
    localStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(LAST_ACTIVITY_KEY)
    if (clearRemember) {
      localStorage.removeItem(REMEMBER_KEY)
    }
  }

  setRememberMe(rememberMe) {
    if (rememberMe) {
      localStorage.setItem(REMEMBER_KEY, 'true')
    } else {
      localStorage.removeItem(REMEMBER_KEY)
    }
  }

  isRemembered() {
    return localStorage.getItem(REMEMBER_KEY) === 'true'
  }

  updateLastActivity() {
    if (this.getToken()) {
      sessionStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString())
    }
  }

  setupActivityMonitor() {
    // Update last activity on user interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
    events.forEach(event => {
      window.addEventListener(event, () => this.updateLastActivity())
    })

    setInterval(async () => {
      if (!(await this.checkSession())) {
        this.redirectToLogin()
      }
    }, 60000)
  }

  async checkSession() {
    if (this.getToken()) {
      try {
        await this.verifyToken()
        return true
      } catch (error) {
        this.clearToken()
        return false
      }
    }
    if (this.isRemembered()) {
      try {
        return await this.refreshToken()
      } catch (error) {
        this.clearToken()
        return false
      }
    }
    return false
  }

  async verifyToken() {
    const token = this.getToken()
    if (!token) {
      throw new Error('No token found')
    }

    // Simple token check - just verify it exists and is properly formatted
    if (!token.startsWith('Token ')) {
      throw new Error('Invalid token format')
    }

    return true
  }

  async refreshToken() {
    if (!this.isRemembered()) {
      return false
    }
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    this.refreshPromise = (async () => {
      const result = await logoutWithRefreshToken()
      if (result && result.token) {
        this.setToken(`Token ${result.token}`, true)
        return true
      }
      return false
    })().finally(() => {
      this.refreshPromise = null
    })
    return this.refreshPromise
  }

  handleTokenError(error) {
    console.error('Token error:', error)
    this.clearToken()
    this.redirectToLogin()
  }

  handleSessionTimeout() {
    this.clearToken()
    this.redirectToLogin()
  }

  redirectToLogin() {
    if (this.redirectingToLogin) {
      return Promise.resolve(false)
    }
    const currentPath = window.location.href
    if (currentPath.includes('#/login') || currentPath.includes('/login')) {
      this.redirectingToLogin = true
      return Promise.resolve(false)
    }
    this.redirectingToLogin = true
    localStorage.setItem('url', currentPath)
    return router.replace('/login').catch(() => false)
  }
}

export const tokenManager = new TokenManager()
export default tokenManager 
